import React, { useMemo, useState } from 'react';
import { DeleteConfirmModal } from '../modals/DeleteConfirmModal';
import { Modal, Button, CloseButton } from '../common';
import type { RoomInfo, Room } from '@/types/room.types';
import roomAPI, { RoomApiError } from '@/services/room-api';
import { formatGuestExpiry, isGuestAccessExpired } from '@/utils/guest-access';
import { 
  FaPlay, 
  FaTrash, 
  FaUsers, 
  FaCrown,
  FaInbox,
  FaExclamationTriangle,
  FaPlus,
  FaWater,
  FaEdit,
  FaSave,
  FaTimes,
  FaBuilding,
  FaClock,
  FaSignOutAlt
} from 'react-icons/fa';

/** Islands with no organization on the DTO are grouped under one heading. */
const UNGROUPED = { id: '__none__', name: 'No organization' };

/**
 * Group the islands by organization, keeping the server's order within a group
 * and sorting the groups by name, so the list reads the same way every time.
 */
function groupByOrganization(rooms: RoomInfo[]): Array<{ id: string; name: string; rooms: RoomInfo[] }> {
  const groups = new Map<string, { id: string; name: string; rooms: RoomInfo[] }>();

  for (const roomInfo of rooms) {
    const organization = roomInfo.room.organization ?? UNGROUPED;
    const group = groups.get(organization.id) ?? { ...organization, rooms: [] };
    group.rooms.push(roomInfo);
    groups.set(organization.id, group);
  }

  return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name));
}

interface RoomListProps {
  rooms: RoomInfo[];
  onJoinRoom: (roomId: string) => Promise<void>;
  onDeleteRoom: (roomId: string) => Promise<void>;
  onCreateRoom: () => void;
  onRoomUpdated?: () => Promise<void>; // Callback to refresh room list after update
  isLoading: boolean;
}

export const RoomList: React.FC<RoomListProps> = ({
  rooms,
  onJoinRoom,
  onDeleteRoom,
  onCreateRoom,
  onRoomUpdated,
  isLoading
}) => {
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    roomId: string;
    roomName: string;
  }>({
    isOpen: false,
    roomId: '',
    roomName: ''
  });

  const [islandDetailsModal, setIslandDetailsModal] = useState<{
    isOpen: boolean;
    room: Room | null;
    userRole?: string;
  }>({
    isOpen: false,
    room: null,
    userRole: undefined
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    description: ''
  });
  const [editError, setEditError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [leaveModal, setLeaveModal] = useState<{ roomId: string; roomName: string } | null>(null);
  const [leaveError, setLeaveError] = useState<string | null>(null);
  const [isLeaving, setIsLeaving] = useState(false);

  const organizationGroups = useMemo(() => groupByOrganization(rooms), [rooms]);

  const handleLeaveConfirm = async () => {
    if (!leaveModal) return;

    setIsLeaving(true);
    setLeaveError(null);

    try {
      await roomAPI.leaveIsland(leaveModal.roomId);
      setLeaveModal(null);
      if (onRoomUpdated) {
        await onRoomUpdated();
      }
    } catch (error) {
      // The last admin of an island cannot walk out of it: somebody has to be
      // able to let people in and clean it up.
      setLeaveError(
        error instanceof RoomApiError && error.code === 'last_admin'
          ? 'You are the last admin — promote someone first'
          : error instanceof Error
            ? error.message
            : 'Failed to leave the island'
      );
    } finally {
      setIsLeaving(false);
    }
  };

  const handleJoinRoom = async (roomId: string) => {
    if (isLoading) return;
    
    try {
      await onJoinRoom(roomId);
    } catch (error) {
      // Error handling is done by parent component
    }
  };

  const handleDeleteClick = (roomId: string, roomName: string) => {
    setDeleteModal({
      isOpen: true,
      roomId,
      roomName
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      await onDeleteRoom(deleteModal.roomId);
    } catch (error) {
      // Error handling is done by parent component
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      roomId: '',
      roomName: ''
    });
  };


  const handleIslandDetailsOpen = (room: Room, userRole?: string) => {
    setIslandDetailsModal({
      isOpen: true,
      room,
      userRole
    });
    // Initialize edit form with current data
    setEditForm({
      name: room.name,
      description: room.description || ''
    });
    setIsEditMode(false);
    setEditError(null);
  };

  const closeIslandDetailsModal = () => {
    setIslandDetailsModal({
      isOpen: false,
      room: null,
      userRole: undefined
    });
    setIsEditMode(false);
    setEditError(null);
  };

  const handleEditToggle = () => {
    if (isEditMode) {
      // Cancel edit - reset form to current room data
      if (islandDetailsModal.room) {
        setEditForm({
          name: islandDetailsModal.room.name,
          description: islandDetailsModal.room.description || ''
        });
      }
      setEditError(null);
    }
    setIsEditMode(!isEditMode);
  };

  const handleFormChange = (field: 'name' | 'description', value: string) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (editError) {
      setEditError(null);
    }
  };

  const handleSaveChanges = async () => {
    if (!islandDetailsModal.room) return;

    // Basic validation
    const trimmedName = editForm.name.trim();
    if (!trimmedName) {
      setEditError('Island name is required');
      return;
    }
    if (trimmedName.length > 86) {
      setEditError('Island name must be at most 86 characters');
      return;
    }
    if (editForm.description.length > 500) {
      setEditError('Description must be at most 500 characters');
      return;
    }

    setIsUpdating(true);
    setEditError(null);

    try {
      await roomAPI.updateRoom(islandDetailsModal.room.id, {
        name: trimmedName,
        description: editForm.description.trim() || undefined
      });

      // Update the modal data with new values
      setIslandDetailsModal(prev => ({
        ...prev,
        room: prev.room ? {
          ...prev.room,
          name: trimmedName,
          description: editForm.description.trim() || undefined
        } : null
      }));

      // Exit edit mode
      setIsEditMode(false);

      // Refresh the room list
      if (onRoomUpdated) {
        await onRoomUpdated();
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update island';
      setEditError(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper function to get display name for roles
  const getRoleDisplayName = (role: string): string => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'Admin';
      case 'moderator':
        return 'Moderator';
      case 'guest':
        return 'Guest';
      case 'member':
      case 'player':
      default:
        return 'Resident';
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-game-teal to-game-green rounded-lg flex items-center justify-center">
                <FaWater className="text-white text-sm" />
              </div>
              <h2 className="text-xl font-bold text-white">Your Islands</h2>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="primary"
                onClick={onCreateRoom}
                disabled={isLoading}
                title="Create new island"
                className="px-3 py-2"
              >
                <FaPlus className="text-sm" />
                <span className="text-sm">Create Island</span>
              </Button>
            </div>
          </div>

        </div>
        
        {rooms.length === 0 ? (
          <div className="bg-gradient-to-b from-game-medium/20 via-game-medium/30 to-game-medium/40 backdrop-blur-md border border-game-medium/50 rounded-lg p-8 text-center shadow-lg">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-game-medium/30 rounded-full flex items-center justify-center">
                <FaInbox className="text-2xl text-game-light" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">No Islands Found</h3>
                <p className="text-gray-200">
                  Create an island, or wait for an invitation to one.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            {organizationGroups.map(group => (
              <section key={group.id}>
                {/* Every island carries its organization, for every role,
                    guests included (spec §4.1). */}
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-game-medium/30">
                  <FaBuilding className="text-game-light text-sm" />
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-game-light">
                    {group.name}
                  </h3>
                  <span className="text-xs text-gray-400">
                    {group.rooms.length === 1 ? '1 island' : `${group.rooms.length} islands`}
                  </span>
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {group.rooms.map((roomInfo) => {
                    const isGuest = roomInfo.role === 'guest';
                    const hasExpired = isGuest && isGuestAccessExpired(roomInfo.expiresAt);

                    return (
                      <div
                        key={roomInfo.room.id}
                        className={`bg-gradient-to-b from-game-medium/20 via-game-medium/30 to-game-medium/40 backdrop-blur-md border border-game-medium/50 rounded-lg p-6 transition-all duration-200 group shadow-lg flex flex-col ${
                          hasExpired
                            ? 'opacity-70'
                            : 'hover:from-game-medium/30 hover:via-game-medium/40 hover:to-game-medium/50 hover:border-game-light/50 hover:shadow-game-medium/20'
                        }`}
                      >
                        {/* Card Content - grows to fill available space */}
                        <div className="flex-grow">
                          {/* Top Bar with Island Name, Role Badge and Player Count */}
                          <div className="flex items-center justify-between mb-4">
                            {/* Island Name */}
                            <button
                              onClick={() => handleIslandDetailsOpen(roomInfo.room, roomInfo.role)}
                              className="text-lg font-semibold text-white truncate group-hover:text-game-golden transition-colors flex-1 min-w-0 pr-3 text-left hover:underline focus:outline-none focus:underline cursor-pointer"
                              title="Click to view full island details"
                            >
                              {roomInfo.room.name}
                            </button>

                            <div className="flex items-center gap-2 flex-shrink-0">
                              {/* Role Badge */}
                              <div className={`flex items-center gap-1.5 px-2 py-0.5 text-xs font-semibold uppercase rounded-full ${
                                roomInfo.role === 'admin'
                                  ? 'bg-gradient-to-r from-game-orange/20 to-game-red/20 border border-game-orange/30 text-game-accent'
                                  : 'bg-game-medium/30 border border-game-medium/50 text-gray-300'
                              }`}>
                                {roomInfo.role === 'admin' && (
                                  <FaCrown className="text-game-golden text-xs" />
                                )}
                                {getRoleDisplayName(roomInfo.role)}
                              </div>

                              {/* Player Count */}
                              <div className="flex items-center gap-1 text-game-golden bg-game-medium/30 px-2 py-1 rounded-lg">
                                <FaUsers className="text-xs" />
                                <span className="text-sm font-semibold">
                                  {roomInfo.room.maxPlayers ? `${roomInfo.playerCount || 0}/${roomInfo.room.maxPlayers}` : '∞'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Organization chip */}
                          <div className="mb-3">
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-full bg-game-dark/40 border border-game-medium/40 text-gray-300">
                              <FaBuilding className="text-[10px]" />
                              {roomInfo.room.organization?.name ?? UNGROUPED.name}
                            </span>
                          </div>

                          {/* Guest window: a date while it lasts, "Expired" after */}
                          {isGuest && (
                            <div className="mb-3">
                              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-full border ${
                                hasExpired
                                  ? 'bg-game-orange/20 border-game-orange/40 text-game-accent'
                                  : 'bg-game-medium/20 border-game-medium/40 text-gray-300'
                              }`}>
                                <FaClock className="text-[10px]" />
                                {hasExpired
                                  ? 'Expired'
                                  : roomInfo.expiresAt
                                    ? `Guest until ${formatGuestExpiry(roomInfo.expiresAt)}`
                                    : 'Guest'}
                              </span>
                            </div>
                          )}

                          {/* Founder Info */}
                          {roomInfo.room.creatorName && (
                            <div className="mb-3">
                              <span className="text-xs text-gray-400">Founder: </span>
                              <span className="text-xs text-gray-300 font-medium">{roomInfo.room.creatorName}</span>
                            </div>
                          )}

                          {/* Room Description */}
                          {roomInfo.room.description && (
                            <p className="text-sm text-gray-200 mb-4 line-clamp-2">
                              {roomInfo.room.description}
                            </p>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 pt-4 border-t border-gray-700/50">
                          {hasExpired ? (
                            <p className="flex-1 text-sm text-gray-400 italic">
                              Ask an admin to extend your guest access.
                            </p>
                          ) : (
                            <Button
                              variant="primary"
                              onClick={() => handleJoinRoom(roomInfo.room.id)}
                              disabled={isLoading}
                              isLoading={isLoading}
                              className="flex-1 px-3 py-2"
                            >
                              <FaPlay className="text-sm" />
                              <span className="text-sm">Join</span>
                            </Button>
                          )}

                          <Button
                            variant="secondary"
                            onClick={() => {
                              setLeaveError(null);
                              setLeaveModal({ roomId: roomInfo.room.id, roomName: roomInfo.room.name });
                            }}
                            disabled={isLoading}
                            title="Leave Island"
                            className="px-3 py-2"
                          >
                            <FaSignOutAlt className="text-sm" />
                          </Button>

                          {roomInfo.role === 'admin' && (
                            <Button
                              variant="danger"
                              onClick={() => handleDeleteClick(roomInfo.room.id, roomInfo.room.name)}
                              disabled={isLoading}
                              title="Delete Island"
                              isLoading={isLoading}
                              className="px-3 py-2"
                            >
                              <FaTrash className="text-sm" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={leaveModal !== null}
        onClose={() => setLeaveModal(null)}
        title="Leave Island"
        size="md"
        className="border-game-orange"
      >
        <div className="space-y-6">
          <div className="p-4 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg">
            <div className="flex items-start gap-3">
              <FaExclamationTriangle className="text-game-golden flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium mb-2">
                  Leave <strong className="text-white">"{leaveModal?.roomName}"</strong>?
                </p>
                <p className="text-sm">
                  You lose access immediately. An admin can invite you back at any time.
                </p>
              </div>
            </div>
          </div>

          {leaveError && (
            <div className="p-3 bg-game-orange/20 border border-game-orange/50 text-game-accent rounded-lg">
              <div className="flex items-center gap-2">
                <FaExclamationTriangle className="text-game-golden flex-shrink-0 text-xs" />
                <span className="font-medium text-sm">{leaveError}</span>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setLeaveModal(null)} disabled={isLeaving}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleLeaveConfirm} isLoading={isLeaving}>
              Leave Island
            </Button>
          </div>
        </div>
      </Modal>

      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        roomName={deleteModal.roomName}
        isLoading={isLoading}
      />

      <Modal
        isOpen={islandDetailsModal.isOpen}
        onClose={closeIslandDetailsModal}
        className="max-w-2xl"
        showCloseButton={false}
      >
        {islandDetailsModal.room && (
          <div className="space-y-6">
            {/* Custom Header - matches modal's p-6 padding */}
            <div className="flex items-center justify-between -mx-6 -mt-6 px-6 py-6 border-b border-game-medium/30">
              <h2 className="text-2xl font-bold text-white">
                {isEditMode ? 'Edit Island Details' : 'Island Details'}
              </h2>
              <div className="flex items-center gap-2">
                {/* Edit Button - only show in view mode */}
                {islandDetailsModal.userRole === 'admin' && !isEditMode && (
                  <Button
                    variant="primary"
                    onClick={handleEditToggle}
                    title="Edit island details"
                    className="px-3 py-2"
                  >
                    <FaEdit className="text-sm" />
                    <span className="text-sm">Edit</span>
                  </Button>
                )}
                
                {/* Close Button */}
                <CloseButton onClick={closeIslandDetailsModal} />
              </div>
            </div>

            {/* Island Name Section */}
            <div>
              {isEditMode ? (
                <div>
                  <label htmlFor="island-name" className="block text-sm font-medium text-gray-300 mb-1">
                    Island Name *
                  </label>
                  <input
                    id="island-name"
                    type="text"
                    value={editForm.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200"
                    placeholder="Enter island name..."
                    maxLength={86}
                    disabled={isUpdating}
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-400">
                      {editForm.name.length}/86 characters
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">
                    {islandDetailsModal.room.name}
                  </h3>
                </div>
              )}
            </div>

            {/* Error Message */}
            {editError && (
              <div className="bg-game-orange/90 backdrop-blur-sm border border-game-orange/50 text-white rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <FaExclamationTriangle className="text-game-golden flex-shrink-0 text-xs" />
                  <span className="font-medium text-sm">{editError}</span>
                </div>
              </div>
            )}

            {/* Founder Info */}
            {islandDetailsModal.room.creatorName && (
              <div className="bg-game-medium/20 border border-game-medium/30 rounded-lg p-3">
                <span className="text-sm text-gray-400">Founded by: </span>
                <span className="text-sm text-gray-200 font-medium">
                  {islandDetailsModal.room.creatorName}
                </span>
              </div>
            )}

            {/* Island Description */}
            <div>
              <h4 className="text-lg font-semibold text-game-light mb-3">Description</h4>
              {isEditMode ? (
                <div>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    className="w-full px-3 py-3 bg-game-cream border border-game-medium/50 rounded-lg text-gray-900 placeholder-gray-600 focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20 focus:outline-none transition-all duration-200 resize-vertical min-h-[120px]"
                    placeholder="Enter a description for your island..."
                    maxLength={500}
                    disabled={isUpdating}
                  />
                  <div className="flex justify-between mt-1">
                    <span className="text-xs text-gray-400">
                      {editForm.description.length}/500 characters
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  {islandDetailsModal.room.description ? (
                    <div className="bg-game-medium/10 border border-game-medium/20 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <p className="text-gray-200 leading-relaxed whitespace-pre-wrap">
                        {islandDetailsModal.room.description}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-game-medium/10 border border-game-medium/20 rounded-lg p-4 text-center">
                      <p className="text-gray-400 italic">No description provided for this island.</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Save/Cancel Buttons - only show in edit mode */}
            {isEditMode && (
              <div className="flex items-center justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={handleEditToggle}
                  disabled={isUpdating}
                  title="Cancel editing"
                  className="px-4 py-2 text-sm"
                >
                  <FaTimes className="text-sm" />
                  <span>Cancel</span>
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveChanges}
                  disabled={isUpdating}
                  isLoading={isUpdating}
                  title="Save changes"
                  className="px-4 py-2"
                >
                  <FaSave className="text-sm" />
                  <span className="text-sm">Save Changes</span>
                </Button>
              </div>
            )}

            {/* Additional Info */}
            <div className="flex flex-col sm:flex-row gap-4 pt-3 border-t border-gray-700/30">
              <div className="bg-game-medium/10 border border-game-medium/20 rounded-lg p-3 flex-1">
                <div className="text-sm text-gray-400 mb-1">Max Players</div>
                <div className="text-game-golden font-semibold">
                  {islandDetailsModal.room.maxPlayers || 'Unlimited'}
                </div>
              </div>
              <div className="bg-game-medium/10 border border-game-medium/20 rounded-lg p-3 flex-1">
                <div className="text-sm text-gray-400 mb-1">Created</div>
                <div className="text-game-golden font-semibold">
                  {new Date(islandDetailsModal.room.createdAt).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};